import { camelize } from '@vimeo/core/libs/camelize';
import { translate } from '@vimeo/i18n-runtime';
import { User } from '@vimeo/viewer';

import { StockClip } from '../types';
import { USER_KEY, VIDEO_KEY } from './constants';
import { idFromUri } from './helpers';
import { trackAddToCart, trackStockDownloadPreview } from './stockGtmSpecs';

type MetaClip = {
  clipId: number;
  clipUri: string;
};

function handleResponse(res: Response) {
  return res
    .text()
    .then((text) => (text ? JSON.parse(text) : {}))
    .then((data) => {
      const camelizedData = camelize<any>(data);
      if (!res.ok || res.status >= 400) {
        throw new Error(res.statusText);
      }
      return camelizedData;
    });
}

const VideoFileApi = {
  download: (clipId: number) => {
    const clipDownloadUrl = `/stock/${clipId}/download_url`;
    return fetch(clipDownloadUrl, {
      credentials: 'include',
    }).then(handleResponse);
  },
};

const CartApi = {
  addToCart: (clipId: number, xsrft: string) => {
    return fetch(`/cart/stock`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ stocks_to_add: [{ clip_id: clipId }], token: xsrft }),
    }).then(handleResponse);
  },
};

const VimeoStockService = {
  /**
   * Downloads a stock clip
   *
   * @param {number} clipId - Id of the clip being downloaded
   */
  downloadStockClip: ({ clipId, name, pictures }: StockClip) => {
    return VideoFileApi.download(clipId).then((response) => {
      if (response.downloadUrl) {
        window.open(response.downloadUrl, '_self');
      } else {
        return new Error(response.error.message);
      }
      // @ts-ignore 'link' missing from expected type but not used in function.
      trackStockDownloadPreview({
        clipId,
        name,
        // @ts-ignore 'picture' as optional value mismatch in StockClip
        pictures,
      });
    });
  },

  /**
   * Adds a stock clip to cart
   *
   * @param {number} clipId - Id of the clip being added to cart
   * @param {string} xsrft - Token
   * @param {number} userId
   */
  addToCart: (clipId: number, xsrft: string, userId: number) => {
    return CartApi.addToCart(clipId, xsrft).then((data) => {
      if (data.error) {
        return data.error as Error;
      }
      let addToCartEvent;
      // @ts-ignore
      if (typeof window.CustomEvent === 'function') {
        addToCartEvent = new CustomEvent('addToCart', { detail: data });
      } else {
        addToCartEvent = window.document.createEvent('CustomEvent');
        addToCartEvent.initCustomEvent('addToCart', true, true, data);
      }
      window.dispatchEvent(addToCartEvent);
      const addedIds = data.items.map((item: MetaClip) => item.clipId);
      VimeoStockService.getStockMetadata(addedIds).then((metadata) => {
        trackAddToCart(clipId, data, metadata, userId);
      });
    });
  },

  /**
   * Fetches stock clip meta data
   *
   * @param {array} clipIds - Ids of the clips
   */
  getStockMetadata: (clipIds: number[]) => {
    const stockMetaDataUri = `/stocks/clips?clip_ids=${clipIds}&fields=title,description,clip_uri,pictures,qualities,page_uri`;

    return fetch(stockMetaDataUri)
      .then((response) => {
        const contentType = response.headers.get('content-type');
        if (contentType && /^application\/.*json/i.test(contentType)) {
          return response.json();
        }

        return response;
      })
      .then<any[]>((response) => camelize(response))
      .then((response) => {
        return response.map((clip) => ({
          ...clip,
          clipId: idFromUri(clip?.clipUri, VIDEO_KEY),
        }));
      });
  },

  /**
   * Adds a free stock clip to account
   *
   * @param {object} User - User object
   * @param {number} clipId - Id of the clip being added to account
   * @param {string} apiUrl - Vimeo Api url
   * @param {string} jwt - API token
   */
  addToAccount: (user: User, clipId: number, apiUrl: string, jwt: string): Promise<Response> => {
    const userId = idFromUri(user?.uri, USER_KEY);
    return fetch(
      `https://${apiUrl}/users/${userId}/purchases/stocks/${clipId}?clip_uri=/videos/${clipId}`,
      {
        headers: {
          Accept: 'application/vnd.vimeo.*+json;version=3.4.1',
          Authorization: `jwt ${jwt}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        credentials: 'omit',
      },
    )
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
        return {
          error: {
            message: translate('Sorry, something weird happened. Please try again.'),
          },
        };
      })
      .then((data) => {
        if (data.error) {
          return data.error;
        }
        return {
          clipId: idFromUri(data?.clip_uri, VIDEO_KEY),
          name: data.title,
          thumbnail: data.pictures.sizes[0].link,
          dimension: data.qualities[0].dimension,
          codec: data.qualities[0].codec,
          quality: data.qualities[0].format,
        };
      });
  },
};

export default VimeoStockService;
